import './child-spotlight-card.scss';

import { Fragment, type ReactNode } from 'react';

import { ChildImage } from '@sola/ark-core/atoms/child-image/child-image.tsx';
import { ChildImageVariantEnum } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';

export interface ChildSpotlightCardProps {
  /** Image element (e.g. `<ChildImage … />`) or a fallback. */
  image?: ReactNode;
  /** Overlay shown in the image pane (e.g. `<Badge … />`). */
  badge?: ReactNode;
  hasRibbon?: boolean;
  children: ReactNode;
}

const DefaultRibbon = () => (
  <div className="ChildSpotlightCard__ribbon" aria-hidden>
    <span />
    <span />
    <span />
    <span />
  </div>
);

export const ChildSpotlightCard: React.FC<ChildSpotlightCardProps> = ({
  image,
  badge,
  hasRibbon = true,
  children,
}) => {
  return (
    <div className="ChildSpotlightCard @container flex flex-col overflow-hidden w-full rounded-lg">
      {hasRibbon && <DefaultRibbon />}
      <div className="ChildSpotlightCard__body flex flex-1 min-h-0 flex-col md:grid md:grid-cols-2">
        <div className="ChildSpotlightCard__imagePane relative overflow-hidden w-full aspect-ratio-16-9 md:h-full md:aspect-ratio-1">
          {image}
          {badge && (
            <div className="ChildSpotlightCard__badge absolute top-comfy-xs left-comfy-xs">
              {badge}
            </div>
          )}
        </div>
        <div className="ChildSpotlightCard__content min-w-0 w-full p-comfy-lg">
          {children}
        </div>
      </div>
    </div>
  );
};

export interface ChildSpotlightImageProps {
  childImageUrl?: string | null;
  alt: string;
  variantMd?: ChildImageVariantEnum;
}

export const ChildSpotlightImage: React.FC<ChildSpotlightImageProps> = ({
  childImageUrl,
  alt,
  variantMd,
}) => {
  if (!childImageUrl) {
    return (
      <div
        className="ChildSpotlightCard__image--fallback w-full h-full"
        aria-hidden
      />
    );
  }

  return (
    <>
      {variantMd && (
        <div aria-hidden="true">
          <ChildImage
            className="block w-full h-full object-fit-cover object-position-top md:hidden"
            childImageUrl={childImageUrl}
            alt={alt}
            variant={ChildImageVariantEnum.Landscape}
            width={900}
          />
        </div>
      )}
      <ChildImage
        className={cn('ChildSpotlightCard__image object-fit-cover block', {
          'ChildSpotlightCard__image--scale sr-only md:not-sr-only':
            !!variantMd,
        })}
        childImageUrl={childImageUrl}
        alt={alt}
        variant={variantMd ?? ChildImageVariantEnum.Landscape}
        width={900}
      />
    </>
  );
};

export interface ChildStatsProps {
  items: {
    icon: ReactNode;
    label: ReactNode;
  }[];
}

export const ChildStats: React.FC<ChildStatsProps> = ({ items }) => (
  <div className="ChildSpotlightCard__stats flex w-full items-stretch justify-center gap-snug-xl">
    {items.map(({ icon, label }, index) => (
      <Fragment key={index}>
        {index > 0 && (
          <span
            className="ChildSpotlightCard__statsDivider self-stretch"
            aria-hidden
          />
        )}
        <span className="ChildSpotlightCard__stat flex min-w-0 flex-col items-center gap-snug-md text-center label-sm label-neutral-mild">
          {icon}
          <span>{label}</span>
        </span>
      </Fragment>
    ))}
  </div>
);

export interface ChildSpotlightCardActionItemProps {
  children: ReactNode;
  className?: string;
}

export const ChildSpotlightCardActionItem: React.FC<
  ChildSpotlightCardActionItemProps
> = ({ children, className }) => (
  <div className={cn('ChildSpotlightCard__actionItem', className)}>
    {children}
  </div>
);
